/**
 * ErrorBoundary Component
 * Coláiste Lurgan Management System - React error boundary for catching JavaScript errors
 */

import React from 'react';
import { AlertTriangle, Home, RefreshCw, Bug } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      errorId: Date.now().toString(36) + Math.random().toString(36).substr(2)
    };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to the console and potentially to an error reporting service
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // Here you could send the error to an error reporting service
    // Example: Sentry.captureException(error, { contexts: { react: errorInfo } });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/dashboard';
  };

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <Card className="max-w-lg w-full">
            <CardContent className="py-8 px-6">
              {/* Error Icon */}
              <div className="flex justify-center mb-6">
                <div className="p-4 bg-destructive/10 rounded-full">
                  <AlertTriangle className="h-12 w-12 text-destructive" />
                </div>
              </div>

              {/* Error Message */}
              <div className="text-center mb-6">
                <h1 className="text-2xl font-semibold mb-2">
                  Something went wrong
                </h1>
                <h2 className="text-lg font-semibold mb-2">
                  Chuaigh rud éigin mícheart
                </h2>

                <p className="text-muted-foreground mb-4">
                  An unexpected error occurred in the application. Please try refreshing the page or contact support if the problem persists.
                </p>

                <p className="text-muted-foreground mb-4">
                  Tharla earráid gan choinne san fheidhmchlár. Bain triail as an leathanach a athnuachan nó déan teagmháil le tacaíocht má leanann an fhadhb.
                </p>
              </div>

              {/* Error ID */}
              {this.state.errorId && (
                <div className="mb-6 p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Bug className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Error ID:</span>
                  </div>
                  <Badge variant="outline" className="font-mono">
                    {this.state.errorId}
                  </Badge>
                </div>
              )}

              {/* Development Error Details */}
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className="mb-6 p-3 bg-muted/50 rounded-lg">
                  <summary className="cursor-pointer text-sm font-medium mb-2">
                    🔧 Development Error Details
                  </summary>
                  <div className="text-xs font-mono bg-background p-2 rounded border overflow-auto max-h-32">
                    <div className="text-destructive mb-2">
                      <strong>Error:</strong> {this.state.error.toString()}
                    </div>
                    {this.state.errorInfo && (
                      <div className="text-muted-foreground">
                        <strong>Component Stack:</strong>
                        <pre className="whitespace-pre-wrap text-xs">
                          {this.state.errorInfo.componentStack}
                        </pre>
                      </div>
                    )}
                  </div>
                </details>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button onClick={this.handleRetry} className="flex items-center gap-2">
                  <RefreshCw className="h-4 w-4" />
                  Try Again / Bain Triail Eile As
                </Button>

                <Button variant="outline" onClick={this.handleReload} className="flex items-center gap-2">
                  <RefreshCw className="h-4 w-4" />
                  Reload Page / Athlódáil
                </Button>

                <Button variant="outline" onClick={this.handleGoHome} className="flex items-center gap-2">
                  <Home className="h-4 w-4" />
                  Go Home / Téigh Abhaile
                </Button>
              </div>

              {/* Coláiste Lurgan Branding */}
              <div className="mt-8 pt-6 border-t text-center text-sm text-muted-foreground">
                Coláiste Lurgan Management System
                <br />
                Córas Bainistíochta Choláiste Lurgan
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    // If no error, render children normally
    return this.props.children;
  }
}

export default ErrorBoundary;